// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { UseMutationResult, useMutation } from '@tanstack/react-query';
import {
  CommercialAgreement,
  CommercialAgreement_Revoke_Result,
} from '@daml.js/utility-commercials-v0/lib/Utility/Commercials/V0/Model/CommercialAgreement';
import { ContractId } from '@daml/types';
import { useLedgerApiClient } from '../../../contexts/LedgerApiContext';
import { useScanProxyApiClient } from '../../../contexts/ScanProxyApiContext';
import { appTransferContextWithDisclosures } from '../../../utils/appTransferContextWithDisclosures';

export const useRevokeCommercialAgreement: () => UseMutationResult<
  CommercialAgreement_Revoke_Result,
  string,
  {
    commercialAgreementCid: ContractId<CommercialAgreement>;
    party: string; // could be user or operator
  }
> = () => {
  const ledgerApi = useLedgerApiClient();
  const scanProxyApi = useScanProxyApiClient();
  return useMutation({
    mutationKey: ['revokeCommercialAgreement', ledgerApi],
    mutationFn: async ({ commercialAgreementCid, party }) => {
      const { appTransferContext, disclosedContracts } = await appTransferContextWithDisclosures(
        scanProxyApi,
        30,
      );
      return ledgerApi.revokeCommercialAgreement(
        commercialAgreementCid,
        appTransferContext,
        party,
        disclosedContracts,
      );
    },
  });
};
